import { useState } from "react";
import { useForm } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import type { Tables } from "@/integrations/supabase/types";

type Invoice = Tables<"invoices">;

interface InvoiceFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoice?: Invoice | null;
  defaultStudentId?: string | null;
  onSuccess: () => void;
}

export function InvoiceForm({ open, onOpenChange, invoice, defaultStudentId, onSuccess }: InvoiceFormProps) {
  const [loading, setLoading] = useState(false);
  const isEditing = !!invoice;

  const { data: students } = useQuery({
    queryKey: ["students-select"],
    queryFn: async () => {
      const { data, error } = await supabase.from("students").select("id, first_name, last_name, student_number").order("last_name");
      if (error) throw error;
      return data;
    },
  });

  const { register, handleSubmit, setValue, watch, reset, formState: { errors } } = useForm({
    defaultValues: {
      student_id: (invoice as any)?.student_id || defaultStudentId || "",
      amount: invoice?.amount?.toString() || "",
      due_date: invoice?.due_date || "",
      description: invoice?.description || "",
      status: invoice?.status || "unpaid",
      kind: (invoice as any)?.kind || "school_fee",
      discount_amount: (invoice as any)?.discount_amount?.toString() || "0",
      advance_payment: (invoice as any)?.advance_payment?.toString() || "0",
      due_amount: (invoice as any)?.due_amount?.toString() || "",
    },
  });

  const onSubmit = async (data: any) => {
    setLoading(true);
    try {
      const payload: any = {
        ...data,
        amount: parseFloat(data.amount),
        discount_amount: parseFloat(data.discount_amount || "0"),
        advance_payment: parseFloat(data.advance_payment || "0"),
        due_amount: data.due_amount === "" ? null : parseFloat(data.due_amount),
      };
      if (payload.due_date === "") payload.due_date = null;
      if (payload.description === "") payload.description = null;
      if (isEditing) {
        const { error } = await supabase.from("invoices").update(payload).eq("id", invoice!.id);
        if (error) throw error;
        toast({ title: "Invoice updated successfully" });
      } else {
        const { error } = await supabase.from("invoices").insert(payload);
        if (error) throw error;
        toast({ title: "Invoice created successfully" });
      }
      reset();
      onOpenChange(false);
      onSuccess();
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit Invoice" : "Create Invoice"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label>Student *</Label>
            <Select value={watch("student_id")} onValueChange={(v) => setValue("student_id", v)}>
              <SelectTrigger><SelectValue placeholder="Select student" /></SelectTrigger>
              <SelectContent position="popper">
                {students?.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.student_number} — {s.first_name} {s.last_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="amount">Amount *</Label>
              <Input id="amount" type="number" step="0.01" {...register("amount", { required: "Required" })} />
              {errors.amount && <p className="text-sm text-destructive">{errors.amount.message as string}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="due_date">Due Date</Label>
              <Input id="due_date" type="date" {...register("due_date")} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label htmlFor="discount_amount" className="text-xs">Discount</Label>
              <Input id="discount_amount" type="number" step="0.01" {...register("discount_amount")} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="advance_payment" className="text-xs">Advance</Label>
              <Input id="advance_payment" type="number" step="0.01" {...register("advance_payment")} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="due_amount" className="text-xs">Due Amount</Label>
              <Input id="due_amount" type="number" step="0.01" placeholder="auto" {...register("due_amount")} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Kind</Label>
              <Select value={watch("kind")} onValueChange={(v) => setValue("kind", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent position="popper">
                  <SelectItem value="school_fee">School Fee</SelectItem>
                  <SelectItem value="other">Other Cost</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={watch("status")} onValueChange={(v) => setValue("status", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent position="popper">
                  <SelectItem value="unpaid">Unpaid</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="overdue">Overdue</SelectItem>
                  <SelectItem value="partial">Partial</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea id="description" {...register("description")} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={loading}>{loading ? "Saving..." : isEditing ? "Update" : "Create"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
